import { useState, type ReactElement } from 'react';
import { FiChevronDown as ChevronDown, FiInfo as Info } from 'react-icons/fi';
import { TbRadar as Radar } from 'react-icons/tb';
import {
  trackerFrequencies,
  trackerResultDepths,
} from '../pricing';
import { keywordOptions, numberFormat, trackerConfig, ui } from '../constants';
import { usePricingTariffs } from '../context/PricingTariffsContext';
import { formatToman } from '../utils';
import type { TrackerFrequencyId, TrackerResultDepth } from '../types';

interface TrackerCardProps {
  enabled: boolean;
  onToggle: (checked: boolean) => void;
  keywords: number;
  onKeywordsChange: (value: string | number) => void;
  frequency: TrackerFrequencyId;
  onFrequencyChange: (id: TrackerFrequencyId) => void;
  resultDepth: TrackerResultDepth;
  onResultDepthChange: (depth: TrackerResultDepth) => void;
  locked?: boolean;
}

function TrackerCard({
  enabled,
  onToggle,
  keywords,
  onKeywordsChange,
  frequency,
  onFrequencyChange,
  resultDepth,
  onResultDepthChange,
  locked = false,
}: TrackerCardProps): ReactElement {
  const { trackerDailyPrice } = usePricingTariffs();
  const [keywordMenuOpen, setKeywordMenuOpen] = useState(false);
  const selectedFrequency = trackerFrequencies.find(
    (item) => item.id === frequency,
  )!;
  const selectedDepth = trackerResultDepths.find(
    (item) => item.depth === resultDepth,
  );
  const depthMultiplier = selectedDepth?.multiplier ?? 1;
  const monthly =
    keywords *
    trackerDailyPrice *
    selectedFrequency.billableDays *
    depthMultiplier;

  const isPurchaseDisabled = !locked && Boolean(trackerConfig.disabled);
  const disabledDescription =
    trackerConfig.disabledDescription ||
    'امکان خریداری این افزونه در دو روز آینده فراهم میشود';
  const isActive = locked || (enabled && !isPurchaseDisabled);
  const controlsDisabled = locked || isPurchaseDisabled || !enabled;

  const card = (
    <section
      className={`tracker-card rounded-4 ${isActive ? 'enabled' : 'bg-white dark__bg-card-dark'} ${
        keywordMenuOpen ? 'menu-open' : ''
      } ${locked ? 'locked' : ''} ${isPurchaseDisabled ? 'purchase-disabled' : ''}`}
      aria-disabled={isPurchaseDisabled || undefined}
    >
      <div className="tracker-top">
        <div className="tracker-title">
          <span className="tracker-icon rounded-3">
            <Radar size={26} />
          </span>
          <div>
            <span className="eyebrow fs--2">{trackerConfig.eyebrow}</span>
            <h2 className="text-1000">{trackerConfig.title}</h2>
            <p className="text-secondary fs--2">{trackerConfig.cardDescription}</p>
          </div>
        </div>

        {locked ? (
          <span className="tracker-locked-badge fs--2 rounded-pill">{ui.common.active}</span>
        ) : (
          <label className={`switch-label fs--2 ${isPurchaseDisabled ? 'disabled' : ''}`}>
            <span className="text-secondary">{enabled ? ui.common.active : ui.common.addToPlan}</span>
            <input
              type="checkbox"
              checked={enabled}
              disabled={isPurchaseDisabled}
              onChange={(event) => onToggle(event.target.checked)}
            />
            <span className="switch rounded-pill" aria-hidden="true">
              <span className="rounded-circle" />
            </span>
          </label>
        )}
      </div>

      <div className="tracker-body">
        <div className="frequency-control">
          <span className="control-label text-900 fs--2">{trackerConfig.labels.frequency}</span>
          <div className="frequency-selector rounded-3">
            {trackerFrequencies.map((item) => (
              <button
                type="button"
                key={item.id}
                className={frequency === item.id ? 'active rounded-2 text-900 fs--2' : 'rounded-2 text-secondary fs--2'}
                onClick={() => onFrequencyChange(item.id)}
                disabled={controlsDisabled}
                aria-pressed={frequency === item.id}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="depth-control">
          <span className="control-label text-900 fs--2">{trackerConfig.labels.depth}</span>
          <div className="depth-selector">
            {trackerResultDepths.map((item) => (
              <button
                type="button"
                key={item.depth}
                className={resultDepth === item.depth ? 'active rounded-3 text-900' : 'rounded-3 text-secondary'}
                onClick={() => onResultDepthChange(item.depth)}
                disabled={controlsDisabled}
                aria-pressed={resultDepth === item.depth}
              >
                <strong>{item.label}</strong>
                <small className="rounded-1">{item.caption}</small>
              </button>
            ))}
          </div>
        </div>

        <div className="keyword-control">
          <span className="control-label text-900 fs--2">{trackerConfig.labels.keywords}</span>
          <div className={`keyword-dropdown ${keywordMenuOpen ? 'open' : ''}`}>
            <button
              type="button"
              className="keyword-dropdown-trigger rounded-3"
              disabled={controlsDisabled}
              aria-haspopup="listbox"
              aria-expanded={keywordMenuOpen}
              onClick={() => {
                if (locked || isPurchaseDisabled) return;
                setKeywordMenuOpen((current) => !current);
              }}
            >
              <strong>{numberFormat.format(keywords)}</strong>
              <span className="text-secondary fs--2">{ui.common.word}</span>
              <ChevronDown size={17} />
            </button>

            {keywordMenuOpen ? (
              <div
                className="keyword-dropdown-menu rounded-3"
                role="listbox"
                aria-label={trackerConfig.labels.keywords}
              >
                {keywordOptions.map((option) => (
                  <button
                    type="button"
                    key={option}
                    role="option"
                    aria-selected={keywords === option}
                    className={keywords === option ? 'selected rounded-2 text-900 fs--2' : 'rounded-2 text-secondary fs--2'}
                    onClick={() => {
                      onKeywordsChange(option);
                      setKeywordMenuOpen(false);
                    }}
                  >
                    {numberFormat.format(option)}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="range-row">
            <span>{numberFormat.format(trackerConfig.keywordRange.min)}</span>
            <input
              type="range"
              min={trackerConfig.keywordRange.min}
              max={trackerConfig.keywordRange.max}
              step={trackerConfig.keywordRange.step}
              value={keywords}
              disabled={controlsDisabled}
              onChange={(event) => onKeywordsChange(event.target.value)}
            />
            <span>{numberFormat.format(trackerConfig.keywordRange.max)}</span>
          </div>
        </div>

        <div className="tracker-price rounded-3">
          <span className="text-secondary fs--2">{trackerConfig.labels.monthlyPrice}</span>
          <strong>{locked ? ui.common.free : formatToman(monthly)}</strong>
        </div>

        {!locked ? (
          <div className="tracker-refund-note fs--2 rounded-3">
            <Info size={16} />
            <span>{trackerConfig.refundNote}</span>
          </div>
        ) : null}
      </div>
    </section>
  );

  if (isPurchaseDisabled) {
    return (
      <div className="tracker-card-wrap tracker-card-wrap--disabled">
        <div className="tracker-card-disabled-tooltip rounded-3 fs--2" role="tooltip">
          {disabledDescription}
        </div>
        {card}
      </div>
    );
  }

  return card;
}

export default TrackerCard;
